import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, User, Clock, Edit, History } from 'lucide-react';
import AuthContext from '../../../../../context/AuthContext';
import { useToast } from '../../../../../context/ToastContext';
import detailedHistoryService from '../../../../../services/detailedHistory';

export default function ProcessDetailedLog() {
  const { processId } = useParams();
  const navigate = useNavigate();
  const { authTokens } = useContext(AuthContext);
  const { showError } = useToast();
  
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchProcessEvolution();
  }, [processId]);

  const fetchProcessEvolution = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await detailedHistoryService.getProcessEvolutionHistory(processId, authTokens.access);
      if (result.success) {
        setHistory(result.data.results);
      } else {
        setError(result.error);
        showError(result.error || 'Erreur lors du chargement de l\'évolution du processus');
      }
    } catch (err) {
      setError('Erreur de connexion');
      showError('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const renderValue = (value) => {
    if (value === null || value === undefined) {
      return 'null';
    }
    if (typeof value === 'object') {
      if (Array.isArray(value)) {
        return `[${value.map(item => renderValue(item)).join(', ')}]`;
      }
      // Handle objects by showing key properties
      if (value.risk_name) return value.risk_name;
      if (value.activity_name) return value.activity_name;
      if (value.process_name) return value.process_name;
      if (value.action) return `Action: ${value.action}`;
      if (value.email) return value.email;
      if (value.name) return value.name;
      return JSON.stringify(value);
    }
    return String(value);
  };

  const renderChanges = (changes) => {
    if (!changes || Object.keys(changes).length === 0) {
      return <p className="text-sm text-gray-500">Aucun changement détaillé enregistré.</p>;
    }

    return (
      <ul className="space-y-2">
        {Object.entries(changes).map(([field, values]) => (
          <li key={field} className="text-sm">
            <strong className="font-semibold text-gray-800">{field}:</strong>
            <span className="ml-2">
              <span className="text-red-600">'{renderValue(values.from)}'</span> 
              <span className="mx-2">→</span> 
              <span className="text-green-600">'{renderValue(values.to)}'</span>
            </span>
          </li>
        ))}
      </ul>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-6"
        >
          <ArrowLeft className="h-5 w-5 mr-2" />
          Retour
        </button>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-2xl font-bold text-gray-900">Évolution Complète du Processus</h1>
            <div className="flex items-center text-gray-500">
              <History className="h-5 w-5 mr-2" />
              <span>{history.length} entrées d'historique</span>
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <p className="text-red-800">{error}</p>
              <button 
                onClick={fetchProcessEvolution}
                className="mt-2 text-red-600 hover:text-red-800 underline"
              >
                Réessayer
              </button>
            </div>
          )}

          {history.length === 0 && !loading && (
            <div className="text-center py-12">
              <History className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <p className="text-lg font-medium text-gray-900">Aucun historique trouvé</p>
              <p className="text-gray-500">Ce processus n'a pas encore d'historique d'évolution.</p>
            </div>
          )}

          <div className="space-y-6">
            {history.map((entry, index) => (
              <div key={entry.id} className="border border-gray-200 rounded-lg p-6 relative">
                {/* Timeline connector */}
                {index < history.length - 1 && (
                  <div className="absolute left-6 top-16 w-0.5 h-6 bg-gray-300"></div>
                )}
                
                <div className="flex items-start space-x-4">
                  <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                    entry.action_type === 'create' ? 'bg-green-100 text-green-600' :
                    entry.action_type === 'update' ? 'bg-blue-100 text-blue-600' :
                    entry.action_type === 'delete' ? 'bg-red-100 text-red-600' :
                    'bg-gray-100 text-gray-600'
                  }`}>
                    <Edit className="h-4 w-4" />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-2">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        entry.action_type === 'create' ? 'bg-green-100 text-green-800' :
                        entry.action_type === 'update' ? 'bg-blue-100 text-blue-800' :
                        entry.action_type === 'delete' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {entry.action_type === 'create' ? 'Création' :
                         entry.action_type === 'update' ? 'Modification' :
                         entry.action_type === 'delete' ? 'Suppression' :
                         entry.action_type}
                      </span>
                      
                      <div className="flex items-center text-sm text-gray-500 space-x-4">
                        <div className="flex items-center">
                          <User className="h-4 w-4 mr-1" />
                          <span>{entry.done_by_email}</span>
                        </div>
                        <div className="flex items-center">
                          <Calendar className="h-4 w-4 mr-1" />
                          <span>{new Date(entry.action_date).toLocaleDateString('fr-FR')}</span>
                        </div>
                        <div className="flex items-center">
                          <Clock className="h-4 w-4 mr-1" />
                          <span>{new Date(entry.action_date).toLocaleTimeString('fr-FR')}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="mb-3">
                      <p className="text-gray-700 font-medium">{entry.description}</p>
                    </div>
                    
                    {entry.changes_summary && (
                      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <h4 className="text-sm font-semibold text-gray-800 mb-2">Changements détaillés:</h4>
                        {renderChanges(entry.changes_summary)}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
